// src/stores/session.ts
import { defineStore } from 'pinia'
import { ref, computed, Ref, watch, unref } from 'vue'
import Api from '@/config/api.helper'
import { push } from 'notivue'
import { useNotificacoesStore } from '@/stores/components/notificacoes'
import { useSaldoConta } from '@/stores/components/useSaldoConta'
import { useOnboardingStore } from '@/stores/components/onboardingStore'
import { useUserStore, type UsuarioStoreData } from './userStore'
import { useAssinaturaStore } from './assinaturaStore'
import { useEmpresaStore } from './empresaStore'
import { usePlanoStore } from '@/stores/components/planoStore'
import { Assinatura } from '@/types/assinatura/assinatura'
import { AxiosResponse } from 'axios'
import { Empresa, Plano, UserData, Usuario, DadosFranquia, Campaings } from '@/types/plugins/session'
import { descriptors } from 'chart.js/dist/core/core.defaults'
import { EmpresaStatusDocumentacaoEnum } from '@/enums/EmpresaStatusDocumentacaoEnum'
import Pusher from 'pusher-js'
import { websocket } from '@/plugins/websocket'
import { PlanosServicosCounter } from '@/types/planos'

export interface DadosFranquiaDTO {
  counter: number
  limit: number
  isExceeded: boolean
  remaining: number
  servico: {
    id: number,
    descricao: string
  }
  modulo: {
    id: number,
    codigo: string,
    titulo_completo: string,
    descricao: string
  }
}

interface Store {
  token: Ref<string | null>
  userData: Ref<UserData | null>
  stringDateDeadlineOldCompanies: string
  isAuthenticated: Ref<boolean>
  isSubscriptionBlocked: Ref<boolean>
  isSubscriptionDefault: Ref<boolean>
  isLegacyCompanyExemptFromContractBlock: Ref<boolean>
  currentUser: Ref<Usuario | null>
  currentEmpresa: Ref<Empresa | null>
  currentPlan: Ref<PlanoWithHelpers | null>
  currentAssinatura: Ref<Assinatura | null>
  currentCampaings: Ref<Campaings[] | null> 
  acessoRestritoModal: Ref<boolean>
  giveAllAccess: Ref<boolean>
  setGiveAllAccess(value: boolean): void
  setToken(newToken: string): void
  removeToken(): void
  setUserData(newUserData: SetUserDataInput): void
  clearSession(): void
  fetchSessionData(): Promise<UserData | null>
  fetchCampaings(): Promise<void>
  updateAssinaturaPlanoServicosCounters(franquias: PlanosServicosCounter[]): void
  updateCampanhasAtivas(campanhas: Campaings[]): void
  updateEmpresaStatusDocumentacao(status: EmpresaStatusDocumentacaoEnum): void
  getFranquiaDados(): DadosFranquiaDTO[]
  switchEmpresa(idEmpresa: string): Promise<boolean>
  initialize(): Promise<void>
}

type ConvertedPlano = {
  id: string
  name: string
  price: number
  newBusinessModel: boolean
  services: any[]
  billingPeriod: string
  isCustomPlan: boolean
}

type PlanoWithHelpers = Plano & {
  // Method to convert to fancy format
  convertToFancy: () => ConvertedPlano
}

type DadosSessaoData = Pick<
  UserData,
  | 'empresasDisponiveis'
  | 'permissoes'
  | 'modulos'
  | 'saldo'
  | 'notificacoesEmpresa'
  | 'onboarding'
>

type DadosSessaoComPlanoData = DadosSessaoData & Pick<UserData, 'plano'>

type SetUserDataInput =
  | DadosSessaoData
  | { data: DadosSessaoData }
  | null

export const useSessionStore = defineStore('session', (): Store => {
  const notificacoesStore = useNotificacoesStore()
  const saldoStore = useSaldoConta()
  const onboardingStore = useOnboardingStore()
  const userStore = useUserStore()
  const assinaturaStore = useAssinaturaStore()
  const empresaStore = useEmpresaStore()
  const planoStore = usePlanoStore()

  const token = ref<string | null>(null)

  const userData = ref<UserData | null>(null)

  let initializedForToken: string | null = null
  let fetchSessionDataInFlight: Promise<UserData | null> | null = null
  let initializeInFlight: Promise<void> | null = null

  const stringDateDeadlineOldCompanies = '2026-10-21 00:00:00';

  const acessoRestritoModal = ref<boolean>(false)
  
  const giveAllAccess = ref<boolean>(false)

  const currentCampaings = ref<Campaings[] | null>(null)

  const isAuthenticated = computed((): boolean => !!token.value)

  const isSubscriptionBlocked = computed((): boolean => {
    return (
      assinaturaStore?.assinatura?.bloqueada ?? false
    )
  })

  const isSubscriptionDefault = computed((): boolean => {
    return (
      planoStore.plano?.pla_descricao?.includes('Padrão') ?? false
    )
  })

  // Empresas cadastradas antes do prazo são isentas do bloqueio/banner de
  // assinatura de contrato — a exigência passou a valer apenas para quem
  // se cadastrar a partir de stringDateDeadlineOldCompanies.
  const isLegacyCompanyExemptFromContractBlock = computed((): boolean => {
    const criadoEm = empresaStore.empresa?.emp_criado_em

    if (!criadoEm) {
      return true
    }

    const dataCriacao = new Date(criadoEm).getTime()
    const dataLimite = new Date(stringDateDeadlineOldCompanies).getTime()

    if (Number.isNaN(dataCriacao) || Number.isNaN(dataLimite)) {
      return true
    }

    return dataCriacao < dataLimite
  })

  function normalizeConfiguracoesUsuario(
    configuracoes: UsuarioStoreData['configuracoes']
  ): Usuario['configuracoes'] {
    if (
      configuracoes &&
      typeof configuracoes === 'object' &&
      Array.isArray((configuracoes as { tema?: unknown }).tema)
    ) {
      const tema = (configuracoes as { tema: unknown[] }).tema.filter(
        (item): item is string => typeof item === 'string'
      )

      return { tema }
    }

    return { tema: [] }
  }

  function mapUserStoreToSessionUsuario(usuario: UsuarioStoreData): Usuario {
    return {
      usu_id: usuario.usu_id,
      uuid_usu_id: usuario.uuid_usu_id,
      usu_avatar: usuario.usu_avatar,
      usu_nome: usuario.usu_nome,
      usu_email: usuario.usu_email,
      usu_seth: usuario.usu_seth,
      usu_senha_automatica: usuario.usu_senha_automatica,
      configuracoes: normalizeConfiguracoesUsuario(usuario.configuracoes),
    }
  }

  function mapSessionUsuarioToUserStoreData(usuario: Usuario): UsuarioStoreData {
    return {
      usu_id: usuario.usu_id,
      uuid_usu_id: usuario.uuid_usu_id,
      usu_avatar: usuario.usu_avatar ?? null,
      usu_nome: usuario.usu_nome,
      usu_email: usuario.usu_email,
      usu_seth: Boolean(usuario.usu_seth),
      usu_senha_automatica: usuario.usu_senha_automatica,
      configuracoes:
        usuario.configuracoes &&
        typeof usuario.configuracoes === 'object' &&
        !Array.isArray(usuario.configuracoes)
          ? { ...usuario.configuracoes }
          : null,
    }
  }

  function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  function extractSessionDataPayload(
    payload: SetUserDataInput
  ): DadosSessaoData| null {
    if (!payload) {
      return null
    }

    if (isRecord(payload) && 'data' in payload && isRecord(payload.data)) {
      return payload.data as DadosSessaoData
    }

    return payload as DadosSessaoData
  }

  function getUserStoreUsuario(): UsuarioStoreData | null {
    const usuario = unref(userStore.usuario)
    return usuario && usuario.uuid_usu_id ? usuario : null
  }

  async function ensureUsuarioFromUserStore(
    forceFetch = false
  ): Promise<Usuario | null> {
    let usuario = getUserStoreUsuario()
    const hasToken = !!token.value || !!localStorage.getItem('token')

    if ((forceFetch || !usuario?.uuid_usu_id) && hasToken) {
      const fetchedUsuario = await userStore.fetchUsuario()
      usuario = fetchedUsuario ?? getUserStoreUsuario()
    }

    if (!usuario?.uuid_usu_id) {
      return null
    }

    return mapUserStoreToSessionUsuario(usuario)
  }

  function getAssinaturaStoreData(): Assinatura | null {
    const assinatura = unref(assinaturaStore.assinatura)
    return assinatura ?? null
  }

  async function ensureAssinaturaFromStore(
    forceFetch = false,
    empresaLogadaId?: string
  ): Promise<Assinatura | null> {
    let assinatura = getAssinaturaStoreData()
    const hasToken = !!token.value || !!localStorage.getItem('token')

    if ((forceFetch || !assinatura) && hasToken) {
      const fetchedAssinatura = await assinaturaStore.fetchAssinatura(
        empresaLogadaId
      )
      assinatura = fetchedAssinatura ?? getAssinaturaStoreData()
    }

    if (!assinatura) {
      return null
    }

    return assinatura
  }

  function getEmpresaStoreData(): Empresa | null {
    const empresa = unref(empresaStore.empresa)

    if (!empresa) {
      return null
    }

    const assinatura = getAssinaturaStoreData()

    if (!assinatura) {
      return empresa
    }

    return {
      ...empresa,
      assinatura,
    }
  }

  async function ensureEmpresaFromStore(
    forceFetch = false
  ): Promise<Empresa | null> {
    let empresa = getEmpresaStoreData()
    const hasToken = !!token.value || !!localStorage.getItem('token')

    if ((forceFetch || !empresa?.uuid_emp_id) && hasToken) {
      const fetchedEmpresa = await empresaStore.fetchEmpresa()
      empresa = fetchedEmpresa ? { ...fetchedEmpresa } : getEmpresaStoreData()
    }

    if (!empresa?.uuid_emp_id) {
      return null
    }

    return empresa
  }

  function getPlanoStoreData(): Plano | null {
    const plano = unref(planoStore.plano)
    return plano && plano.uuid_pla_id ? plano : null
  }

  async function ensurePlanoFromStore(
    forceFetch = false,
    empresaLogadaId?: string
  ): Promise<Plano | null> {
    let plano = getPlanoStoreData()
    const hasToken = !!token.value || !!localStorage.getItem('token')

    if ((forceFetch || !plano?.uuid_pla_id) && hasToken) {
      const fetchedPlano = await planoStore.fetchPlano(empresaLogadaId)
      plano = fetchedPlano ?? getPlanoStoreData()
    }

    if (!plano?.uuid_pla_id) {
      return null
    }

    return plano
  }


  const currentUser = computed(
    (): UserData['usuario'] | null => {
      const usuario = getUserStoreUsuario()
      if (usuario) {
        return mapUserStoreToSessionUsuario(usuario)
      }

      return null
    }
  )

  const currentEmpresa = computed(
    (): UserData['empresa'] | null => {
      const empresaFromStore = getEmpresaStoreData()

      if (empresaFromStore) {
        return empresaFromStore
      }

      return null
    }
  )

  const currentAssinatura = computed(
    (): Assinatura | null => {
      return getAssinaturaStoreData()
    }
  )

  const currentPlan = computed(
    (): PlanoWithHelpers | null => {
      const plan = userData.value?.plano ?? null
      if (!plan) return null

      // If method already attached, return as-is
      if ((plan as PlanoWithHelpers).convertToFancy) {
        return plan as PlanoWithHelpers
      }

      const fancy: PlanoWithHelpers = plan as PlanoWithHelpers

      Object.defineProperty(fancy, 'convertToFancy', {
        enumerable: false,
        configurable: true,
        writable: false,
        value: function (): ConvertedPlano {
          //console.log('Converting plan to fancy format:', this)
          return {
            id: this?.uuid_pla_id,
            name: this?.pla_descricao,
            price: this?.monthly_price ?? 0,
            newBusinessModel: !!this?.new_business_model,
            isCustomPlan: !!this?.is_custom_plan,
            services: this?.services || [],
            billingPeriod: this?.newBusinessModel ? '/mês' : '',
          }
        },
      })

      return fancy
    }
  )

  function setGiveAllAccess(value: boolean) {
    giveAllAccess.value = value
  }

  function setToken(newToken: string) {
    const previousToken = token.value
    token.value = newToken
    localStorage.setItem('token', newToken)

    if (previousToken !== newToken) {
      initializedForToken = null
    }
  }

  function removeToken() {
    token.value = null
    initializedForToken = null
    localStorage.removeItem('token')
  }

  function setUserData(newUserData: SetUserDataInput) {
    const normalizedUserData = extractSessionDataPayload(newUserData)

    if (normalizedUserData) {
      const usuarioFromStore = getUserStoreUsuario()
      const assinaturaFromStore = getAssinaturaStoreData()
      const empresaFromStore = getEmpresaStoreData()
      const planoFromStore = getPlanoStoreData()

      if (!usuarioFromStore || !empresaFromStore || !planoFromStore) {
        console.error(
          'Não foi possível montar userData: dados de usuário, empresa e plano devem vir dos stores específicos.'
        )
        return
      }

      const usuarioEfetivo = mapUserStoreToSessionUsuario(usuarioFromStore)
      const assinaturaEfetiva = assinaturaFromStore ?? empresaFromStore.assinatura
      const empresaEfetiva = {
        ...empresaFromStore,
        assinatura: assinaturaEfetiva,
      }

      assinaturaStore.setAssinatura(assinaturaEfetiva)
      empresaStore.setEmpresa(empresaEfetiva)
      planoStore.setPlano(planoFromStore)
      userStore.setUsuario(mapSessionUsuarioToUserStoreData(usuarioEfetivo))

      userData.value = {
        ...normalizedUserData,
        usuario: usuarioEfetivo,
        empresa: empresaEfetiva,
        plano: planoFromStore,
      } as UserData

      notificacoesStore.defineNotificacoesEmpresa(
        userData.value.notificacoesEmpresa
      )
      saldoStore.setSaldo(userData.value.saldo)
      onboardingStore.setOnboardingList(userData.value.onboarding || [])

      return
    }

    userData.value = null
    userStore.clearUsuario()
    empresaStore.clearEmpresa()
    assinaturaStore.clearAssinatura()
    planoStore.clearPlano()
  }

  function updateEmpresaStatusDocumentacao(status: EmpresaStatusDocumentacaoEnum){
    const empresa = getEmpresaStoreData()

    if (empresa && userData.value) {
      const updatedEmpresa = {
        ...empresa,
        emp_status_documentacao: status,
      }

      empresaStore.setEmpresa(updatedEmpresa)

      const dadosSessaoAtual = {
        empresasDisponiveis: userData.value.empresasDisponiveis,
        permissoes: userData.value.permissoes,
        modulos: userData.value.modulos,
        saldo: userData.value.saldo,
        notificacoesEmpresa: userData.value.notificacoesEmpresa,
        onboarding: userData.value.onboarding,
      }

      setUserData({
        ...dadosSessaoAtual,
      })
    }
  }

  function clearSession() {
    removeToken()
    setUserData(null)
  }

  async function fetchSessionData(): Promise<UserData | null> {
    if (fetchSessionDataInFlight) {
      return fetchSessionDataInFlight
    }

    fetchSessionDataInFlight = (async () => {
      try {
        const usuario = await ensureUsuarioFromUserStore(true)
        const empresa = await ensureEmpresaFromStore(true)

        if (!usuario) {
          console.error('Usuário não encontrado no userStore para montar a sessão.')
          return null
        }

        if (!empresa) {
          console.error('Empresa não encontrada no empresaStore para montar a sessão.')
          return null
        }

        const empresaLogadaId = empresa.uuid_emp_id
        await ensureAssinaturaFromStore(true, empresaLogadaId)
        const plano = await ensurePlanoFromStore(true, empresaLogadaId)

        if (!plano) {
          console.error('Plano não encontrado no planoStore para montar a sessão.')
          return null
        }

        const response = (await Api.get(
          'v2/auth/dados-sessao-new',
          {
            headers: {
              empresalogada: empresaLogadaId,
            },
          }
        )) as { data: { data: DadosSessaoData } }

        const dadosSessao = response.data.data
        const dadosSessaoSemPlano: DadosSessaoData = {
          empresasDisponiveis: dadosSessao.empresasDisponiveis,
          permissoes: dadosSessao.permissoes,
          modulos: dadosSessao.modulos,
          saldo: dadosSessao.saldo,
          notificacoesEmpresa: dadosSessao.notificacoesEmpresa,
          onboarding: dadosSessao.onboarding,
        }

        setUserData({ data: dadosSessaoSemPlano })

        const activeToken = token.value || localStorage.getItem('token')
        if (activeToken && userData.value) {
          initializedForToken = activeToken
        }

        return userData.value
      } catch (error) {
        console.error('Erro ao buscar dados da sessão:', error)
        return null
      }
    })()

    try {
      return await fetchSessionDataInFlight
    } finally {
      fetchSessionDataInFlight = null
    }
  }

  async function fetchCampaings(): Promise<void> {
    const empresaId = currentEmpresa.value?.emp_id

    if (!empresaId) {
      currentCampaings.value = []
      return
    }

    try {
      const response = (await Api.get(
        `v2/empresa/campanhas?situacao[]=Pausar&empresa_id[]=${empresaId}&per_page=100&ativa=true`,
        {
          skipUnauthorizedRedirect: true,
        }
      )) as AxiosResponse<{ data: Campaings[] }>
      const fullCampaings = response.data.data as Campaings[]
      //console.log('Campaings ativas:', fullCampaings)
      // Assign a new array reference to ensure Vue reactivity picks up the change
      currentCampaings.value = Array.isArray(fullCampaings)
        ? JSON.parse(JSON.stringify(fullCampaings))
        : []
    } catch (error) {
      console.error('Erro ao buscar campanhas ativas:', error)
    }
  }

  function getFranquiaDados(): any {
    const dados_franquia = currentPlan.value?.dados_franquia;
    const uso_franquia = currentEmpresa.value?.assinatura?.planos_servicos_counter;
    const franquia = [
      /*
      {
        counter: 0,
        limit: 0,
        isExceeded: false,
        servico: {
          id: 0,
          descricao: ''
        }
      }
      */
    ];
    if (Array.isArray(dados_franquia)) {
      
      for(var dado_franquia of dados_franquia){
        if (Array.isArray(uso_franquia)){
          const uso_obj = uso_franquia.find((uso_fqa) => uso_fqa?.modulo.codigo === dado_franquia?.modulo?.codigo);
          const counter = uso_obj ? uso_obj.uses_count || 0 : 0;
          const limit = dado_franquia?.franchise_limit || 0;
          const isExceeded = limit >= 0 ? counter >= limit : false;

          franquia.push({
            counter: counter,
            limit: limit,
            remaining: limit - counter,
            isExceeded: isExceeded,
            servico: JSON.parse(JSON.stringify(dado_franquia?.modulo.titulo_completo)),
            modulo: JSON.parse(JSON.stringify(dado_franquia?.modulo))
          });
        }
      }
    }
    return franquia;
  }

  function updateAssinaturaPlanoServicosCounters(franquias: PlanosServicosCounter[]) {
    const copyOfUserData = userData.value;

    assinaturaStore.updatePlanosServicosCounter(franquias)

    const empresa = getEmpresaStoreData()

    if (empresa?.assinatura) {
      empresaStore.updateAssinatura({
        ...empresa.assinatura,
        planos_servicos_counter: franquias,
      })
    }

    if (copyOfUserData?.empresa?.assinatura) {
      userData.value = {
        ...copyOfUserData,
        empresa: {
          ...copyOfUserData.empresa,
          assinatura: {
            ...copyOfUserData.empresa.assinatura,
            planos_servicos_counter: franquias,
          },
        },
      }
    }
  }

  function updateCampanhasAtivas(campanhas: Campaings[]) {
    // Ensure we replace with a fresh array reference (deep clone)
    currentCampaings.value = Array.isArray(campanhas)
      ? JSON.parse(JSON.stringify(campanhas))
      : []
  }

  async function initialize(): Promise<void> {
    if (initializeInFlight) {
      await initializeInFlight
      return
    }

    initializeInFlight = (async () => {
      const localToken = localStorage.getItem('token')

      if (!localToken) {
        return
      }

      token.value = localToken

      if (userData.value && initializedForToken === localToken) {
        return
      }

      await fetchSessionData()
    })()

    try {
      await initializeInFlight
    } finally {
      initializeInFlight = null
      await fetchCampaings()
    }
  }

  async function switchEmpresa(idEmpresa: string): Promise<boolean> {
    if (!currentUser.value?.uuid_usu_id) {
      console.error('ID do usuário não encontrado para trocar de empresa.')
      return false
    }

    // Desconecta o websocket antes de trocar de empresa e gerar novo token
    websocket.disconnectWebSocket()

    const payload = {
      uuid_usu_id: currentUser.value.uuid_usu_id,
      uuid_emp_id: idEmpresa,
    }

    try {
      const response = await Api.post('v2/auth/altera-empresa-logada', payload)
      const responseData = response.data as {
        data?: { token?: string }
        token?: string
      }
      const regeneratedToken = responseData?.data?.token ?? responseData?.token

      const empresaLogadaId = idEmpresa

      if (!empresaLogadaId) {
        console.error(
          'UUID da empresa logada não encontrado para sincronizar sessão após troca de empresa.'
        )
        push.error('Erro ao sincronizar empresa logada')
        
        // Em caso de falha, tentamos reconectar se havia se desconectado
        websocket.connectWebSocket()
        return false
      }

      if (!regeneratedToken) {
        console.error('Token não retornado ao alterar empresa logada.')
        push.error('Erro ao regenerar sessão da empresa logada')
        
        // Em caso de falha, tentamos reconectar se havia se desconectado
        websocket.connectWebSocket()
        return false
      }

      setToken(regeneratedToken)
      const refreshedSessionData = await fetchSessionData()
      
      // Conecta o websocket COM a nova empresa apenas no final, após sucesso total
      if (refreshedSessionData) {
        websocket.connectWebSocket()
        // Recarrega as campanhas da nova empresa
        await fetchCampaings()
      }
      
      return !!refreshedSessionData
    } catch (e) {
      console.error('Erro ao alterar empresa logada:', e)
      push.error('Erro ao alterar empresa logada')
      
      // Em caso de falha, tentamos reconectar se havia se desconectado
      websocket.connectWebSocket()
      return false
    }
  }

  watch(
    () => userData.value,
    (newUserData) => {
      const localExibirAcessoRestritoModal = localStorage.getItem(
        'exibirAcessoRestritoModal'
      )

      //Se localExibirAcessoRestritoModal for diferente de 'true', não exibe o modal
      if (
        localExibirAcessoRestritoModal &&
        localExibirAcessoRestritoModal !== 'true'
      ) {
        acessoRestritoModal.value = false
        return
      }

      //Se newUserData.empresa não existir, não exibe o modal
      if (!newUserData?.empresa) {
        acessoRestritoModal.value = false
        return
      }

      if (newUserData.empresa.emp_documentacao_status === true || !userData.value?.plano?.pla_descricao?.includes('Padrão')) {
        acessoRestritoModal.value = false
        return
      }
      
      acessoRestritoModal.value = true
    }
  )

  return {
    token,
    userData,
    stringDateDeadlineOldCompanies,
    isAuthenticated,
    isSubscriptionBlocked,
    isSubscriptionDefault,
    isLegacyCompanyExemptFromContractBlock,
    currentUser,
    currentEmpresa,
    currentPlan,
    currentCampaings,
    acessoRestritoModal,
    giveAllAccess,
    currentAssinatura,
    fetchCampaings,
    setToken,
    removeToken,
    setGiveAllAccess,
    setUserData,
    clearSession,
    fetchSessionData,
    updateAssinaturaPlanoServicosCounters,
    updateCampanhasAtivas,
    updateEmpresaStatusDocumentacao,
    getFranquiaDados,
    switchEmpresa,
    initialize,
  }
})
